/* eslint-disable @typescript-eslint/no-explicit-any */
import { message } from 'antd';
import i18next from 'i18next';
import { IState } from 'providers/ReportValues/types';

import { instance } from 'utils/api';

export const LANGUAGE_KEYS = {
  en: 'en-US',
  de: 'de-DE'
};

export const handleBeforeUpload = (
  file: File,
  onSignChange: (image: string) => void,
  setError: (value: boolean) => void
) => {
  return new Promise<File>(resolve => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result as string;
      onSignChange(base64String);
      resolve(file);
      setError(false);
    };

    reader.readAsDataURL(file);
  });
};

export const uploadAudio = async (
  file: File,
  lang: string,
  setValues: (values: IState) => void,
  form: any,
  values: IState,
  setCreatingTranscript: (state: boolean) => void
) => {
  const data = new FormData();
  data.append('file', file);

  try {
    setCreatingTranscript(true);
    const res = await instance.post(
      'voice-to-text/upload-and-transcribe',
      data,
      {
        params: { language: LANGUAGE_KEYS[lang] },
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    );

    if (res?.data?.transcript) {
      const value = values.report + res?.data?.transcript;
      setValues({ ...values, report: value });
      form.setFieldValue('report', value);
    }
  } catch (error: any) {
    message.error(error.message);
  } finally {
    setCreatingTranscript(false);
  }
};

export const onFinishRecording = async (
  result: string,
  lang: string,
  setValues: (values: IState) => void,
  form: any,
  values: IState,
  canceledRecording: boolean,
  setCreatingTranscript: (state: boolean) => void
) => {
  const abortController = new AbortController();
  const signal = abortController.signal;

  try {
    if (canceledRecording) {
      abortController.abort();

      return;
    }

    setCreatingTranscript(true);

    const response = await fetch(result, { signal });

    if (canceledRecording) {
      abortController.abort();

      return;
    }

    const blob = await response.blob();
    const [, extension] = blob.type.split('/');

    const file = new File([blob], `sound.${extension}`, { type: blob.type });

    const data = new FormData();
    data.append('file', file);

    const res = await instance.post(
      'voice-to-text/upload-and-transcribe',
      data,
      {
        params: { language: LANGUAGE_KEYS[lang] },
        headers: { 'Content-Type': 'multipart/form-data' },
        signal
      }
    );

    if (res?.data?.transcript) {
      const value = values.report + res?.data?.transcript;
      setValues({ ...values, report: value });
      form.setFieldValue('report', value);
    }
  } catch (error: any) {
    message.error(error.message || 'An error occurred while processing.');
    setValues({ ...values, report: '' });
  } finally {
    setCreatingTranscript(false);
  }
};

export const validateFileSize = (file: File, maxFileSize: number): boolean => {
  const isWithinSizeLimit = file.size / 1024 / 1024 < maxFileSize;

  if (!isWithinSizeLimit) {
    message.error(i18next.t('reports.file_too_large', { size: maxFileSize }));
  }

  return isWithinSizeLimit;
};
